/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */

import { useRef, useEffect, useState, useMemo } from "react";
import { initMixpanelEvents } from "jiffy-analytics";
import { objNamesToSnakeCase } from "common/utils/helpers/cases";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import { cn } from "tailwind-variants";
import JiffyServicePreview from "./JiffyServicePreview.vue";
import JiffyPromotionTransfers from "./JiffyPromotionTransfers.vue";
import JiffyPromotionBlanks from "./JiffyPromotionBlanks.vue";
import { renderList } from "common/vue/render-list";
import "./JiffyServicesPreview.scss";
import ArrowLeftTinyIcon from "assets/icons/arrow-left-tiny.svg";
import ArrowRightTinyIcon from "assets/icons/arrow-right-tiny.svg";

const buildPreviewUrl = (path: any) =>
  remoteImageStaticUrl(path, {
    ixlib: "rb-0.3.5",
    h: 100,
    w: 100,
    dpr: 2,
    q: 30
  });

export default function JiffyServicesPreview({
  services,
  design,
  mixpanelEvent,
  source,
  headerText = "",
  openInNewTab = true,
  showTransfersStoreCreditBanner = false,
  showBlanksFreeShippingBanner = false,
  ...restProps
}: any) {
  // State
  const [hasArrows, setHasArrows] = useState(false);
  const [position, setPosition] = useState("left");

  // Service data definitions
  const transfers = {
    title: "Fabric Transfers (DTF)",
    preview: buildPreviewUrl("gen/jiffy-services-preview/transfers.png"),
    icon: "jiffy-transfers-inline",
    page_url: "/",
    cta: "Upload Image",
    additional_mixpanel_event: "transfers_cta_click",
    business_line_promo_type: "Transfers",
    promotion_name: "fabric_transfers",
    price_from: "$0",
    price_per: "$0",
    price_per_label: "per sq.\ninch",
    service: "transfers"
  };

  const made = {
    title: "Custom Tees & Sweatshirts",
    preview: buildPreviewUrl("gen/jiffy-services-preview/made.png"),
    icon: "jiffy-made-inline",
    page_url: "/",
    cta: "Upload Image",
    business_line_promo_type: "Made",
    promotion_name: "custom_tees_sweatshirts",
    price_from: "$0",
    price_per: "$0",
    price_per_label: "all-in\nper shirt",
    service: "made",
    additional_mixpanel_event: "print_cta_click"
  };

  const stickers = {
    title: "Die-Cut Vinyl Stickers",
    preview: buildPreviewUrl("gen/jiffy-services-preview/stickers.png"),
    icon: "jiffy-stickers-inline",
    page_url: "/",
    cta: "Add Stickers",
    additional_mixpanel_event: "stickers_cta_click",
    business_line_promo_type: "Stickers",
    promotion_name: "die_cut_vinyl_stickers",
    price_from: "$0",
    price_per: "$0",
    price_per_label: "per sq.\ninch",
    service: "stickers"
  };

  const blanks = {
    title: "Shirts",
    preview: buildPreviewUrl("gen/jiffy-services-preview/blanks-no-logo.jpg"),
    icon: "jiffy-shirts-inline",
    page_url: "/",
    cta: "Add to cart",
    additional_mixpanel_event: "blanks_cta_click",
    business_line_promo_type: "Blanks",
    promotion_name: "shirts",
    price_per: "$0",
    price_from: "$0",
    service: "blanks"
  };

  // Refs
  const scrollWrapperRef = useRef(null);
  const promotionTransfersRef = useRef(null);
  const promotionBlanksRef = useRef(null);
  const servicesRefsMap = useRef(new Map());

  // Computed properties as functions
  function preparedServices() {
    return Object.values(services).sort((serviceA, serviceB) => {
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      if (serviceA.service === "transfers") return -1;
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      if (serviceB.service === "transfers") return 1;
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      if (serviceA.service === "made") return -1;
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      if (serviceB.service === "made") return 1;
      return 0;
    });
  }

  function mergedServices() {
    const serviceData = { transfers, made, stickers, blanks };
    const merged = preparedServices().map(service => {
      // @ts-expect-error TS(2339): Property 'service' does not exist on type 'unknown... Remove this comment to see the full error message
      const { service: key } = service;
      return {
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        ...(key in serviceData ? serviceData[key] : blanks),
        // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
        ...service
      };
    });
    return objNamesToSnakeCase(merged);
  }

  function isLargeUpsell() {
    return design === "large";
  }

  // Methods
  function outputSize() {
    const elem = scrollWrapperRef.current;
    if (!elem) return;
    const { clientWidth, scrollWidth } = elem;
    setHasArrows(scrollWidth > clientWidth);
  }

  function handleArrowClick(direction: any, overscrollValue = 50) {
    const elem = scrollWrapperRef.current;
    if (!elem) return;

    const { scrollLeft, clientWidth } = elem;
    const scrollRight = scrollLeft + clientWidth;
    let elementHiddenLeft;
    let elementHiddenRight: any;

    const serviceRefs = Array.from(servicesRefsMap.current.values());
    [promotionBlanksRef.current, promotionTransfersRef.current, ...serviceRefs]
      .filter(Boolean)
      .forEach(service => {
        const element = service;
        const elementLeft = element?.offsetLeft || 0;
        const elementRight = elementLeft + (element?.offsetWidth || 0);
        if (elementLeft < scrollLeft) {
          elementHiddenLeft = element;
        }
        if (!elementHiddenRight && elementRight > scrollRight) {
          elementHiddenRight = element;
        }
      });

    // @ts-expect-error TS(2339): Property 'scrollTo' does not exist on type 'never'... Remove this comment to see the full error message
    elem.scrollTo({
      left:
        direction === "left"
          ? // @ts-expect-error TS(2339): Property 'offsetLeft' does not exist on type 'neve... Remove this comment to see the full error message
            (elementHiddenLeft?.offsetLeft || 0) - overscrollValue
          : (elementHiddenRight?.offsetLeft || 0) - overscrollValue,
      behavior: "smooth"
    });
  }

  function handleSliderScroll() {
    const elem = scrollWrapperRef.current;
    if (!elem) return;

    const { scrollWidth, scrollLeft, clientWidth } = elem;
    if (scrollLeft === 0) {
      setPosition("left");
    } else if (scrollLeft + clientWidth >= scrollWidth - 1) {
      setPosition("right");
    } else {
      setPosition("center");
    }
  }

  function disableArrow(direction: any) {
    return position === direction || !hasArrows;
  }

  // Lifecycle
  useEffect(() => {
    initMixpanelEvents(".jiffy-services-preview");

    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => outputSize());
    });

    if (scrollWrapperRef.current) {
      resizeObserver.observe(scrollWrapperRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Memoize merged services to avoid excessive recomputation
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mergedServices is stable (depends only on services prop)
  const merged = useMemo(() => mergedServices(), [services]);

  return (
    <div
      {...restProps}
      className={cn(
        "jiffy-services-preview scope-16ee0bb0",
        `jiffy-services-preview--design-${design}`
      )}
    >
      {headerText ? (
        <div className="jiffy-services-preview__title scope-16ee0bb0">{headerText}</div>
      ) : null}
      <div className="jiffy-services-preview__arrows-wrapper scope-16ee0bb0">
        <div
          className="jiffy-services-preview__scroll-wrapper scope-16ee0bb0"
          ref={scrollWrapperRef}
          onScroll={() => {
            handleSliderScroll();
          }}
          data-testid="jiffy-services-preview-scroll-wrapper"
        >
          <div className="jiffy-services-preview__previews-wrapper scope-16ee0bb0">
            {showTransfersStoreCreditBanner ? (
              <JiffyPromotionTransfers
                className="scope-16ee0bb0"
                ref={promotionTransfersRef}
                spendThreshold={50}
                creditValue={20}
                openInNewTab={openInNewTab}
                data-testid="jiffy-promotion-transfers"
              />
            ) : null}
            {showBlanksFreeShippingBanner ? (
              <JiffyPromotionBlanks
                className="scope-16ee0bb0"
                ref={promotionBlanksRef}
                spendThreshold={50}
                openInNewTab={openInNewTab}
                data-testid="jiffy-promotion-blanks"
              />
            ) : null}
            {renderList(merged, service => (
              <JiffyServicePreview
                className="scope-16ee0bb0"
                ref={(el: any) => {
                  if (el) {
                    servicesRefsMap.current.set(service.service, el);
                  }
                }}
                key={service.service}
                service={service}
                mixpanelEvent={mixpanelEvent}
                mixpanelData={JSON.stringify({
                  page: "cart"
                })}
                source={source}
                design={design}
                horizontal={merged.length === 1}
                showFromPrice={service.show_from_price}
                showProduct={isLargeUpsell()}
                openInNewTab={openInNewTab}
                data-testid="jiffy-service-preview"
              />
            ))}
          </div>
        </div>
        {!isLargeUpsell() ? (
          <button
            className="jiffy-services-preview__arrow jiffy-services-preview__arrow--left scope-16ee0bb0"
            aria-label="Slide left"
            disabled={disableArrow("left")}
            data-direction="left"
            type="button"
            onClick={() => {
              handleArrowClick("left");
            }}
            data-testid="jiffy-services-preview-arrow"
          >
            <ArrowLeftTinyIcon className="scope-16ee0bb0" data-testid="use-svg" />
          </button>
        ) : null}
        {!isLargeUpsell() ? (
          <button
            className="jiffy-services-preview__arrow jiffy-services-preview__arrow--right scope-16ee0bb0"
            aria-label="Slide right"
            data-direction="right"
            type="button"
            disabled={disableArrow("right")}
            onClick={() => {
              handleArrowClick("right");
            }}
            data-testid="jiffy-services-preview-arrow-1"
          >
            <ArrowRightTinyIcon className="scope-16ee0bb0" data-testid="use-svg-1" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
